import React, { type JSX } from "react";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { Autocomplete, AutocompleteRenderGetTagProps, Checkbox, Chip, TextField } from "@mui/material";
import { styled, SxProps, Theme } from "@mui/material/styles";
import { OptionEntry } from "../../../types/cqa";

type Props = {
  disabled?: boolean;
  options: Array<OptionEntry>;
  selectedValues: Array<OptionEntry>;
  onChange: (selectedValues) => void;
  getOptionLabel?: (option) => string;
  isOptionEqualToValue?: (option, value) => boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
  filterSelectedOptions?: boolean;
  useCheckboxRender?: boolean;
  customDisplayChip?: (index: number, label: string, getTagProps: AutocompleteRenderGetTagProps) => JSX.Element;
};

const defaultGetOptionLabel = (option: unknown): string => {
  if(typeof option === "object" && "label" in option) {
    return option.label as string;
  }

  return option as string;
};

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const FullWidthAutocomplete = styled(Autocomplete)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    width: '100%',
  },
});

const MultiSelectAutocomplete = ({
  disabled = false,
  options = [],
  selectedValues = [],
  onChange = (selectedValues: any[]) => {},
  getOptionLabel = defaultGetOptionLabel,
  isOptionEqualToValue = (option, value) => option?.value === value?.value,
  placeholder = "Select options",
  sx = {},
  filterSelectedOptions = false,
  useCheckboxRender = false,
  customDisplayChip = null,
}: Props) => {

  return (
    <FullWidthAutocomplete
      disabled={disabled}
      multiple
      freeSolo
      options={options}
      value={selectedValues}
      filterSelectedOptions={filterSelectedOptions}
      onChange={(change, value) => onChange(value as any)}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      sx={{ ...sx, width: '100%' }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;

        if (useCheckboxRender) {
          return (
            <li key={key} {...optionProps}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {getOptionLabel(option)}
            </li>
          );
        }

        return (
          <li key={key} {...optionProps}>
            <span style={{ width: "100%" }}>{getOptionLabel(option)}</span>
          </li>
        );
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => {
          if(customDisplayChip) {
            return customDisplayChip(index, getOptionLabel(option), getTagProps);
          }

          return (
            <Chip
              key={index}
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={placeholder}
          size="small"
          sx={{ width: '100%' }}
        />
      )}
      slotProps={{
        popper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              width: '100%',
            }
          }
        }
      }}
    />
  );
};

export default MultiSelectAutocomplete;